export const SITE_SETTINGS_ROLES = ["developer", "administrator", "tenant_admin", "super_admin"] as const;
export const CATALOG_ADMIN_ROLES = ["administrator", "tenant_admin", "super_admin"] as const;
// `email_account` is the canonical role; `email_user` is retained for JWTs
// issued by the earlier identity contract (`EMAIL_USER`, case-insensitively).
export const EMAIL_USER_ROLES = ["email_account", "email_user"] as const;

type Claims = Record<string, unknown> | null | undefined;

const ROLE_ALIASES: Record<string, string> = {
  administrators: "administrator",
  developers: "developer",
  email_accounts: "email_account",
  email_users: "email_user",
  super_admins: "super_admin",
  super_administrator: "super_admin",
  super_administrators: "super_admin",
  tenant_admins: "tenant_admin",
  tenant_administrator: "tenant_admin",
  tenant_administrators: "tenant_admin",
};

export function normalizeRoleValue(value: string): string {
  const normalized = value.trim().toLowerCase().replace(/[\s-]+/g, "_");
  return ROLE_ALIASES[normalized] ?? normalized;
}

function claimValues(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap(claimValues);
  }

  if (typeof value === "string") {
    return [value];
  }

  if (value && typeof value === "object") {
    return Object.values(value as Record<string, unknown>).flatMap(claimValues);
  }

  return [];
}

export function roleValuesFromClaims(claims: Claims): string[] {
  if (!claims) {
    return [];
  }

  const roles = new Set<string>();

  Object.entries(claims).forEach(([key, value]) => {
    if (!key.toLowerCase().includes("role")) {
      return;
    }

    claimValues(value).forEach((role) => {
      role.split(",").forEach((part) => {
        const normalized = normalizeRoleValue(part);
        if (normalized) roles.add(normalized);
      });
    });
  });

  return Array.from(roles);
}

export function hasAnyRole(claims: Claims, allowedRoles: readonly string[]): boolean {
  const roles = roleValuesFromClaims(claims);
  const allowed = allowedRoles.map(normalizeRoleValue);
  return allowed.some((role) => roles.includes(role));
}

export function hasSiteSettingsRole(claims: Claims): boolean {
  return hasAnyRole(claims, SITE_SETTINGS_ROLES);
}

export function hasCatalogAdminRole(claims: Claims): boolean {
  return hasAnyRole(claims, CATALOG_ADMIN_ROLES);
}

export function hasEmailUserRole(claims: Claims): boolean {
  return hasAnyRole(claims, EMAIL_USER_ROLES);
}
